import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { 
  BarChart3, 
  Users, 
  Eye, 
  TrendingUp, 
  MousePointerClick,
  FileText,
  Heart,
  RefreshCw,
  Loader2,
  ArrowLeft,
  Globe,
  Smartphone,
  Monitor,
  Tablet
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const COLORS = ['#8b5cf6', '#f97316', '#22c55e', '#3b82f6', '#ec4899', '#eab308'];

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: '7days', label: 'Last 7 Days' },
  { value: '30days', label: 'Last 30 Days' },
  { value: 'all', label: 'All Time' },
];

export default function AdminAnalyticsPage() {
  const navigate = useNavigate();
  const { isPlatformOwner, getAuthHeaders } = useAuth();
  const [period, setPeriod] = useState('7days');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [pageViews, setPageViews] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [sources, setSources] = useState([]);
  const [devices, setDevices] = useState([]);
  const [donations, setDonations] = useState(null);

  useEffect(() => {
    if (!isPlatformOwner) {
      navigate('/');
      return;
    }
    fetchAnalytics();
  }, [isPlatformOwner, period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      
      // Fetch all analytics data in parallel
      const [statsRes, pageViewsRes, topPagesRes, sourcesRes, devicesRes, donationsRes] = await Promise.all([
        fetch(`${API}/admin/analytics/stats?period=${period}`, { headers }),
        fetch(`${API}/admin/analytics/pageviews?period=${period}`, { headers }),
        fetch(`${API}/admin/analytics/top-pages?period=${period}&limit=10`, { headers }),
        fetch(`${API}/admin/analytics/sources?period=${period}`, { headers }),
        fetch(`${API}/admin/analytics/devices?period=${period}`, { headers }),
        fetch(`${API}/admin/donations/stats?period=${period}`, { headers }),
      ]);

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      
      if (pageViewsRes.ok) {
        const data = await pageViewsRes.json();
        setPageViews(data.data || []);
      }
      
      if (topPagesRes.ok) {
        const data = await topPagesRes.json();
        setTopPages(data.pages || []);
      }
      
      if (sourcesRes.ok) {
        const data = await sourcesRes.json();
        setSources(data.sources || []);
      }
      
      if (devicesRes.ok) {
        const data = await devicesRes.json();
        setDevices(data.devices || []);
      }
      
      if (donationsRes.ok) {
        const data = await donationsRes.json();
        setDonations(data);
      }
    } catch (err) {
      console.error('[AdminAnalytics] Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalytics();
    setRefreshing(false);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const getDeviceIcon = (device) => {
    switch (device?.toLowerCase()) {
      case 'mobile': return Smartphone;
      case 'tablet': return Tablet;
      default: return Monitor;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-6" data-testid="admin-analytics-page">
      <div className="max-w-[1800px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/control-center')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-primary" />
                Analytics Dashboard
              </h1>
              <p className="text-muted-foreground text-sm">Site traffic and engagement metrics</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[150px]" data-testid="analytics-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleRefresh} disabled={refreshing} data-testid="analytics-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="p-4 glass-card border-border" data-testid="stat-pageviews">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Eye className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wider">Page Views</span>
            </div>
            <p className="text-2xl font-bold">{formatNumber(stats?.page_views || 0)}</p>
          </Card>
          
          <Card className="p-4 glass-card border-border" data-testid="stat-visitors">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Users className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wider">Unique Visitors</span>
            </div>
            <p className="text-2xl font-bold">{formatNumber(stats?.unique_visitors || 0)}</p>
          </Card>
          
          <Card className="p-4 glass-card border-border" data-testid="stat-requests">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <MousePointerClick className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wider">Requests</span>
            </div>
            <p className="text-2xl font-bold">{formatNumber(stats?.requests_submitted || 0)}</p>
          </Card>
          
          <Card className="p-4 glass-card border-border" data-testid="stat-profile-views">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <FileText className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wider">Profile Views</span>
            </div>
            <p className="text-2xl font-bold">{formatNumber(stats?.profile_views || 0)}</p>
          </Card>
          
          <Card className="p-4 glass-card border-border" data-testid="stat-donations-count">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Heart className="w-4 h-4 text-red-500" />
              <span className="text-xs uppercase tracking-wider">Donations</span>
            </div>
            <p className="text-2xl font-bold">{formatNumber(donations?.count || 0)}</p>
          </Card>
          
          <Card className="p-4 glass-card border-border" data-testid="stat-donations-total">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <TrendingUp className="w-4 h-4 text-green-500" />
              <span className="text-xs uppercase tracking-wider">Total Donated</span>
            </div>
            <p className="text-2xl font-bold text-green-500">${donations?.total?.toFixed(2) || '0.00'}</p>
          </Card>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Page Views Over Time */}
          <Card className="p-6 glass-card border-border" data-testid="chart-pageviews">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Page Views Over Time
            </h3>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={pageViews}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#666"
                    tick={{ fill: '#999', fontSize: 12 }}
                  />
                  <YAxis 
                    stroke="#666"
                    tick={{ fill: '#999', fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1a1a1a', 
                      border: '1px solid #333',
                      borderRadius: '8px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="views" 
                    stroke="#8b5cf6" 
                    strokeWidth={2}
                    dot={{ fill: '#8b5cf6' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="unique" 
                    stroke="#f97316" 
                    strokeWidth={2}
                    dot={{ fill: '#f97316' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-6 mt-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-primary" />
                <span className="text-muted-foreground">Page Views</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-secondary" />
                <span className="text-muted-foreground">Unique Visitors</span>
              </div>
            </div>
          </Card>

          {/* Top Pages */}
          <Card className="p-6 glass-card border-border" data-testid="chart-top-pages">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-secondary" />
              Top Pages
            </h3>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topPages} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis type="number" stroke="#666" tick={{ fill: '#999', fontSize: 12 }} />
                  <YAxis 
                    type="category" 
                    dataKey="path" 
                    stroke="#666" 
                    tick={{ fill: '#999', fontSize: 11 }}
                    width={150}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1a1a1a', 
                      border: '1px solid #333',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="views" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Traffic Sources */}
          <Card className="p-6 glass-card border-border" data-testid="chart-sources">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-500" />
              Traffic Sources
            </h3>
            <div className="h-[300px] flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sources}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="source"
                  >
                    {sources.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1a1a1a', 
                      border: '1px solid #333',
                      borderRadius: '8px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-4">
              {sources.map((source, index) => (
                <div key={source.source} className="flex items-center gap-2 text-sm">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="text-muted-foreground capitalize">{source.source}</span>
                  <span className="font-medium">{source.count}</span>
                </div>
              ))}
            </div>
          </Card>

          {/* Devices */}
          <Card className="p-6 glass-card border-border" data-testid="chart-devices">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Monitor className="w-5 h-5 text-cyan-500" />
              Device Breakdown
            </h3>
            <div className="space-y-4">
              {devices.map((device, index) => {
                const Icon = getDeviceIcon(device.device);
                const total = devices.reduce((sum, d) => sum + d.count, 0);
                const percentage = total > 0 ? ((device.count / total) * 100).toFixed(1) : 0;
                
                return (
                  <div key={device.device} className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-muted/30 flex items-center justify-center">
                      <Icon className="w-5 h-5" style={{ color: COLORS[index % COLORS.length] }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between mb-1">
                        <span className="font-medium capitalize">{device.device}</span>
                        <span className="text-muted-foreground">{percentage}%</span>
                      </div>
                      <div className="h-2 bg-muted/50 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{ 
                            width: `${percentage}%`,
                            backgroundColor: COLORS[index % COLORS.length]
                          }}
                        />
                      </div>
                    </div>
                    <span className="font-bold w-16 text-right">{formatNumber(device.count)}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Donations Table */}
        <Card className="p-6 glass-card border-border" data-testid="donations-section">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Heart className="w-5 h-5 text-red-500" />
            Recent Donations
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="p-4 bg-green-500/10 border-green-500/30">
              <p className="text-sm text-muted-foreground">All Time</p>
              <p className="text-2xl font-bold text-green-500">${donations?.total_all_time?.toFixed(2) || '0.00'}</p>
            </Card>
            <Card className="p-4 bg-blue-500/10 border-blue-500/30">
              <p className="text-sm text-muted-foreground">Last 30 Days</p>
              <p className="text-2xl font-bold text-blue-500">${donations?.total_30_days?.toFixed(2) || '0.00'}</p>
            </Card>
            <Card className="p-4 bg-purple-500/10 border-purple-500/30">
              <p className="text-sm text-muted-foreground">Last 7 Days</p>
              <p className="text-2xl font-bold text-purple-500">${donations?.total_7_days?.toFixed(2) || '0.00'}</p>
            </Card>
          </div>

          {donations?.recent && donations.recent.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4">Date</th>
                    <th className="text-left py-3 px-4">Amount</th>
                    <th className="text-left py-3 px-4">Type</th>
                    <th className="text-left py-3 px-4">Donor</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {donations.recent.map((donation, idx) => (
                    <tr key={idx} className="border-b border-border hover:bg-muted/30">
                      <td className="py-3 px-4">
                        {new Date(donation.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 font-bold text-green-500">
                        ${donation.amount?.toFixed(2)}
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className={donation.is_recurring ? 'border-purple-500 text-purple-500' : ''}>
                          {donation.is_recurring ? 'Monthly' : 'One-time'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {donation.donor_name || 'Anonymous'}
                      </td>
                      <td className="py-3 px-4">
                        <Badge 
                          className={
                            donation.status === 'completed' ? 'bg-green-500/20 text-green-500' :
                            donation.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                            'bg-red-500/20 text-red-500'
                          }
                        >
                          {donation.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-muted-foreground max-w-[200px] truncate">
                        {donation.note || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No donations yet
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
